// ===================================================================
// src/app/services/auth.service.ts
//
// Serviço de autenticação com dois modos:
//   - API real (VITE_API_URL definida)
//   - Mock local (sem VITE_API_URL)
//
// O mock simula o contrato da API para dev sem backend.
// ===================================================================

import { ENV } from '../lib/env';
import { api, setTokens, clearTokens, getRefreshToken, ApiError } from '../lib/api';

// ── Types ────────────────────────────────────────────────────────

export interface UserProfileSector {
  id: string;
  name: string;
  slug: string;
  color: string;
}

export interface UserProfile {
  id: number;
  school: string;
  school_name: string;
  department: string | null;
  department_name: string | null;
  role: 'manager' | 'coordinator' | 'operator' | 'end_user';
  role_display: string;
  sectors: UserProfileSector[];
  is_active: boolean;
  phone: string;
  date_of_birth: string | null;
  /** URL presigned (R2) da foto de perfil — null se não configurada.
   * Expira e é regerada a cada fetch do usuário — nunca cachear além da
   * sessão em memória. */
  avatar_url: string | null;
  notification_digest_enabled: boolean;
}

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_superuser: boolean;
  is_staff: boolean;
  profile: UserProfile | null;
}

export interface LoginResponse {
  access: string;
  refresh: string;
  user: User;
  message: string;
}

export interface LoginCredentials {
  /** O campo "email" do form é enviado como "username" para a API */
  username: string;
  password: string;
}

// ── Mock data ────────────────────────────────────────────────────

const MOCK_USER: User = {
  id: 1,
  username: 'gestor.demo',
  email: 'gestor@eleveedemo.com.br',
  first_name: 'Carlos',
  last_name: 'Gestor',
  is_superuser: false,
  is_staff: false,
  profile: {
    id: 1,
    school: '00000000-0000-0000-0000-000000000001',
    school_name: 'Escola Demo Aletheia',
    department: null,
    department_name: null,
    role: 'manager',
    role_display: 'Gestor',
    sectors: [],
    is_active: true,
    phone: '21999999999',
    date_of_birth: '1985-03-15',
    avatar_url: null,
    notification_digest_enabled: false,
  },
};

const MOCK_TOKENS = {
  access: 'mock_access_token_nexora_dev',
  refresh: 'mock_refresh_token_nexora_dev',
};

// ── Mock helpers ─────────────────────────────────────────────────

function delay(ms = 800): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ── Auth service ─────────────────────────────────────────────────

export const authService = {
  /**
   * Login — envia username + password.
   *
   * ⚠️  A API espera `username`, não `email`.
   *     O form pode usar email como identificador,
   *     mas precisa enviar no campo `username`.
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    // ── Mock mode ──────────────────────────────────────────
    if (ENV.isMockMode) {
      await delay();

      // Aceita qualquer credencial não-vazia no mock
      if (!credentials.username || !credentials.password) {
        throw new ApiError(400, { detail: 'Preencha todos os campos.' });
      }

      const response: LoginResponse = {
        ...MOCK_TOKENS,
        user: MOCK_USER,
        message: 'Login successful',
      };

      setTokens(response.access, response.refresh);
      return response;
    }

    // ── API real ───────────────────────────────────────────
    const response = await api<LoginResponse>('/auth/login/', {
      method: 'POST',
      body: credentials,
      noAuth: true,
    });

    setTokens(response.access, response.refresh);
    return response;
  },

  /**
   * Logout — blacklista o refresh token no backend.
   */
  async logout(): Promise<void> {
    if (ENV.isMockMode) {
      await delay(300);
      clearTokens();
      return;
    }

    const refresh = getRefreshToken();

    try {
      if (refresh) {
        await api('/auth/logout/', {
          method: 'POST',
          body: { refresh },
        });
      }
    } finally {
      clearTokens();
    }
  },

  /**
   * Busca o perfil do usuário autenticado.
   */
  async getProfile(): Promise<User> {
    if (ENV.isMockMode) {
      await delay(200);
      return MOCK_USER;
    }

    // noRedirect: true — só chamado por checkSession() (verificação silenciosa
    // no mount do AuthProvider). Sem isso, um token expirado faz o lib/api.ts
    // navegar via window.location.href='/login' antes do React sequer
    // renderizar a rota atual — quebrando deep links públicos como /invite/:token.
    return api<User>('/auth/profile/', { noRedirect: true });
  },

  /**
   * Verifica se há sessão ativa (token válido no localStorage).
   * Em mock, verifica se existe o mock token.
   * Em API, tenta buscar o profile — se falhar, sessão expirou.
   */
  async checkSession(): Promise<User | null> {
    const { getAccessToken } = await import('../lib/api');
    const token = getAccessToken();

    if (!token) return null;

    if (ENV.isMockMode) {
      return MOCK_USER;
    }

    try {
      return await this.getProfile();
    } catch {
      return null;
    }
  },
};
